import { ChangeDetectionStrategy, Component, Input, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonIcon, ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { PING_ROUTE } from 'src/api/ping-route';
import { normalizeIpv4 } from '../ip-utils';

/**
 * A small "ping this address" icon button, shown next to any IP address in the UI.
 *
 * Renders nothing when the host is missing or unusable, or when the app has no ping page
 * to reach. The target route comes from the PING_ROUTE token so this works unchanged in
 * both the device dashboard and the cloud portal.
 */
@Component({
    selector: 'app-ping-link',
    standalone: true,
    imports: [RouterModule, ButtonModule, ButtonIcon, TooltipModule],
    template: `
        @if (target(); as t) {
            <a
                [routerLink]="t.link"
                [queryParams]="t.queryParams"
                pButton
                type="button"
                severity="secondary"
                [text]="true"
                [iconOnly]="true"
                size="small"
                class="ping-link"
                [pTooltip]="'Ping ' + normalized()"
                tooltipPosition="top"
                [attr.aria-label]="'Ping ' + normalized()"
                (click)="$event.stopPropagation()"
            >
                <!-- The pButton directive has no icon input (unlike the p-button component);
                     it finds its icon through a content query for pButtonIcon. Passing
                     icon="..." as a plain attribute renders an empty button. -->
                <span pButtonIcon class="fa fa-signal-stream"></span>
            </a>
        }
    `,
    styles: [
        `
            .ping-link {
                margin-left: 0.25rem;
                vertical-align: middle;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PingLinkComponent {
    /** Optional in the app config: pages still render if no ping route is provided. */
    private pingRoute = inject(PING_ROUTE, { optional: true });

    @Input() set host(value: string | null | undefined) {
        this._host.set(value ?? null);
    }
    private _host = signal<string | null>(null);

    /** Which device should run the test. Cloud pages listing several devices pass the row's id. */
    @Input() set deviceId(value: string | null | undefined) {
        this._deviceId.set(value ?? null);
    }
    private _deviceId = signal<string | null>(null);

    /** Accepts zero-padded addresses (as produced by the processor's IP table). */
    normalized = computed(() => {
        const raw = (this._host() ?? '').trim();
        if (!raw) return null;
        return normalizeIpv4(raw) ?? raw;
    });

    target = computed(() => {
        const host = this.normalized();
        if (!host || !this.pingRoute) return null;
        return this.pingRoute(host, this._deviceId() ?? undefined);
    });
}
